import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  FC,
} from "react";
import * as React from "react";
import cn from "classnames";
import Card from "@ebsco-ui/card";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import {
  faExclamationCircle,
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp, IconDefinition } from "@fortawesome/fontawesome-svg-core";
import InPageHeader from "./in-page-header";
import InPageDescription from "./in-page-description";
import { InPageContextProvider } from "./in-page-context";

import "./in-page-notification.scss";

const ICONS = {
  default: faExclamationCircle,
  error: faExclamationCircle,
  information: faExclamationCircle,
  success: faCheckCircle,
  warning: faExclamationTriangle,
};

enum VARIANT {
  error = "error",
  information = "information",
  success = "success",
  warning = "warning",
}

export interface InPageNotificationProps {
  ariaLabel?: string;
  children?: React.ReactChild | React.ReactChild[];
  className?: string;
  icon?: IconProp;
  variant: keyof typeof VARIANT;
}

export interface InPageNotificationComposite
  extends ForwardRefExoticComponent<
    InPageNotificationProps & RefAttributes<HTMLElement>
  > {
  ICONS: {
    warning: IconDefinition;
    success: IconDefinition;
    error: IconDefinition;
    information: IconDefinition;
    default: IconDefinition;
  };
  VARIANT: typeof VARIANT;
  Header: FC;
  Description: FC;
}

const InPageNotification = forwardRef<HTMLElement, InPageNotificationProps>(
  ({ ariaLabel = "", children = [], className = "", variant, icon }, ref) => {
    const faIcon = (icon as IconProp) || ICONS[variant] || ICONS.default;
    const getBlocksWith = createBemBlockBuilder([
      "eb-in-page-notification",
      className,
    ]);

    const inPageContext = {
      getBlocksWith,
    };

    return (
      <InPageContextProvider value={inPageContext}>
        <Card
          ariaLabel={ariaLabel || variant}
          border="all"
          className={cn(getBlocksWith(), getBlocksWith(`--${variant}`))}
          isRounded
          isTransparent={false}
          ref={ref}
        >
          <FontAwesomeIcon
            data-auto="font-awesome-icon"
            className={getBlocksWith("__icon")}
            icon={faIcon}
          />
          <div data-auto="content" className={getBlocksWith("__content")}>
            {children}
          </div>
        </Card>
      </InPageContextProvider>
    );
  },
) as InPageNotificationComposite;

InPageNotification.displayName = "InPageNotification";
InPageNotification.ICONS = ICONS;
InPageNotification.VARIANT = VARIANT;
InPageNotification.Header = InPageHeader;
InPageNotification.Header.displayName = "InPageNotification.Header";
InPageNotification.Description = InPageDescription;
InPageNotification.Description.displayName = "InPageNotification.Description";

export default InPageNotification;
